import { useState } from 'react';
import { FaCog, FaUser, FaBell, FaShieldAlt, FaPalette, FaGlobe } from 'react-icons/fa';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState({
    theme: 'dark',
    language: 'de',
    notifications: true,
    publicProfile: false,
    twoFactor: false,
    timeZone: 'Europe/Berlin'
  });

  const tabs = [
    { id: 'general', label: 'Allgemein', icon: <FaCog />, color: 'from-blue-500 to-blue-600' },
    { id: 'profile', label: 'Profil', icon: <FaUser />, color: 'from-green-500 to-green-600' },
    { id: 'notifications', label: 'Benachrichtigungen', icon: <FaBell />, color: 'from-yellow-500 to-orange-600' },
    { id: 'security', label: 'Sicherheit', icon: <FaShieldAlt />, color: 'from-red-500 to-red-600' },
    { id: 'appearance', label: 'Erscheinungsbild', icon: <FaPalette />, color: 'from-purple-500 to-purple-600' },
  ];

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const renderGeneralSettings = () => (
    <div className="space-y-3 sm:space-y-6">
      <div className="modern-card p-3 sm:p-6">
        <h3 className="text-sm sm:text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2 sm:mb-4 flex items-center">
          <FaGlobe className="mr-1.5 sm:mr-3 text-blue-500 text-xs sm:text-base" />
          Sprache & Region
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">Sprache</label>
            <select 
              className="w-full px-2 sm:px-4 py-1.5 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent dark:bg-gray-700 dark:text-white text-xs sm:text-base"
              value={settings.language}
              onChange={(e) => handleSettingChange('language', e.target.value)}
            >
              <option value="de">Deutsch</option>
              <option value="en">English</option>
              <option value="fr">Français</option>
            </select>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">Zeitzone</label>
            <select 
              className="w-full px-2 sm:px-4 py-1.5 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent dark:bg-gray-700 dark:text-white text-xs sm:text-base"
              value={settings.timeZone}
              onChange={(e) => handleSettingChange('timeZone', e.target.value)}
            >
              <option value="Europe/Berlin">Europa/Berlin</option>
              <option value="Europe/London">Europa/London</option>
              <option value="America/New_York">Amerika/New York</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderProfileSettings = () => (
    <div className="space-y-3 sm:space-y-6">
      <div className="modern-card p-3 sm:p-6">
        <h3 className="text-sm sm:text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2 sm:mb-4">Profil-Sichtbarkeit</h3>
        <div className="space-y-2 sm:space-y-4">
          <div className="flex items-center justify-between p-2 sm:p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex-1 mr-2 sm:mr-3">
              <h4 className="font-medium text-gray-800 dark:text-gray-200 text-xs sm:text-base">Öffentliches Profil</h4>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Andere Nutzer können dein Profil einsehen</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer"
                checked={settings.publicProfile}
                onChange={(e) => handleSettingChange('publicProfile', e.target.checked)}
              />
              <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-500/20 dark:peer-focus:ring-blue-400/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500 dark:peer-checked:bg-blue-400"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-3 sm:space-y-6">
      <div className="modern-card p-3 sm:p-6">
        <h3 className="text-sm sm:text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2 sm:mb-4">Benachrichtigungseinstellungen</h3>
        <div className="space-y-2 sm:space-y-4">
          <div className="flex items-center justify-between p-2 sm:p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex-1 mr-2 sm:mr-3">
              <h4 className="font-medium text-gray-800 dark:text-gray-200 text-xs sm:text-base">E-Mail-Benachrichtigungen</h4>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Erhalte Updates per E-Mail</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer"
                checked={settings.notifications}
                onChange={(e) => handleSettingChange('notifications', e.target.checked)}
              />
              <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-500/20 dark:peer-focus:ring-blue-400/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500 dark:peer-checked:bg-blue-400"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-3 sm:space-y-6">
      <div className="modern-card p-3 sm:p-6">
        <h3 className="text-sm sm:text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2 sm:mb-4">Sicherheitseinstellungen</h3>
        <div className="space-y-2 sm:space-y-4">
          <div className="flex items-center justify-between p-2 sm:p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex-1 mr-2 sm:mr-3">
              <h4 className="font-medium text-gray-800 dark:text-gray-200 text-xs sm:text-base">Zwei-Faktor-Authentifizierung</h4>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Zusätzliche Sicherheit für dein Konto</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer"
                checked={settings.twoFactor}
                onChange={(e) => handleSettingChange('twoFactor', e.target.checked)}
              />
              <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-500/20 dark:peer-focus:ring-blue-400/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500 dark:peer-checked:bg-blue-400"></div>
            </label>
          </div>
          <div className="p-2 sm:p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
            <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-1 sm:mb-2 text-xs sm:text-base">Passwort ändern</h4>
            <p className="text-xs sm:text-sm text-blue-600 dark:text-blue-300 mb-2 sm:mb-3">Regelmäßige Passwort-Updates erhöhen die Sicherheit</p>
            <button className="glass-btn text-white px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-lg">
              Passwort ändern
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAppearanceSettings = () => (
    <div className="space-y-3 sm:space-y-6">
      <div className="modern-card p-3 sm:p-6">
        <h3 className="text-sm sm:text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2 sm:mb-4">Design & Erscheinungsbild</h3>
        <div className="space-y-2 sm:space-y-4">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 sm:mb-3">Theme</label>
            <div className="grid grid-cols-2 gap-1.5 sm:gap-3">
              {[
                { id: 'light', label: 'Hell', preview: 'bg-white border-2' },
                { id: 'dark', label: 'Dunkel', preview: 'bg-gray-800 border-2' }
              ].map(theme => (
                <label key={theme.id} className="cursor-pointer">
                  <input 
                    type="radio" 
                    name="theme" 
                    value={theme.id}
                    checked={settings.theme === theme.id}
                    onChange={(e) => handleSettingChange('theme', e.target.value)}
                    className="sr-only"
                  />
                  <div className={`p-2 sm:p-4 rounded-lg border-2 transition-all ${
                    settings.theme === theme.id 
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400' 
                      : 'border-gray-200 hover:border-gray-300 dark:border-gray-600 dark:hover:border-gray-500'
                  }`}>
                    <div className={`w-full h-8 sm:h-16 rounded ${theme.preview} mb-1 sm:mb-2`}></div>
                    <p className="text-center font-medium text-gray-800 dark:text-gray-200 text-xs sm:text-sm">{theme.label}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general': return renderGeneralSettings();
      case 'profile': return renderProfileSettings();
      case 'notifications': return renderNotificationSettings();
      case 'security': return renderSecuritySettings();
      case 'appearance': return renderAppearanceSettings();
      default: return renderGeneralSettings();
    }
  };

  return (
    <div className="min-h-screen">
      <div className="w-full px-0 sm:px-4 py-3 sm:py-8">
        {/* Header */}
        <div className="text-center mb-4 sm:mb-8 px-2">
          <h1 className="text-xl sm:text-4xl font-bold text-white mb-2">
            Einstellungen
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-base">Passe deine Forum-Erfahrung an deine Bedürfnisse an</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-3 sm:gap-8">
          {/* Sidebar with Tabs */}
          <div className="lg:w-64">
            <div className="modern-card p-2 sm:p-4">
              <nav className="space-y-1 sm:space-y-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-1.5 sm:gap-3 p-1.5 sm:p-3 rounded-lg sm:rounded-xl transition-all duration-200 text-left text-xs sm:text-base ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700 text-white shadow-lg'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    <div className={`p-1 sm:p-2 rounded ${
                      activeTab === tab.id 
                        ? 'bg-white/20' 
                        : `bg-gradient-to-r ${tab.color} text-white`
                    }`}>
                      {tab.icon}
                    </div>
                    <span className="font-medium">{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-grow">
            <div className="animate-fadeIn">
              {renderTabContent()}
            </div>
            {/* Save Button */}
            <div className="modern-card p-3 sm:p-6 mt-3 sm:mt-6">
              <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-4">
                <button className="px-3 sm:px-6 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-xs sm:text-base">
                  Zurücksetzen
                </button>
                <button className="glass-btn text-white px-3 sm:px-6 py-2 sm:py-3 rounded-lg font-medium text-xs sm:text-base">
                  Änderungen speichern
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
